import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, MessageCircle, Mail, Send, Loader2, Instagram } from "lucide-react";
import { Reveal, SectionHeading } from "./Reveal";
import { SITE, API, THERAPIES, waLink } from "@/lib/site";

const EMPTY = { name: "", phone: "", email: "", therapy: "", message: "" };

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || null,
        therapy: form.therapy || null,
        message: form.message.trim(),
      });
      toast.success("Thank you! Your message has reached NAVYOGA — we will reach out shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error("Could not send your message. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-emerald-900/15 bg-white px-4 py-3.5 text-sm text-slate-800 outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-slate-400 focus:border-[#2E8B57] focus:ring-4 focus:ring-[#2E8B57]/10";

  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-20 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <SectionHeading
          testId="contact-heading"
          overline="Visit / Contact"
          title="Begin your wellness journey today"
          description="Walk in for a conversation, message us on WhatsApp, or send the form — Dr. Rajni Ahuja personally responds to every enquiry."
        />

        <div className="mt-14 grid gap-10 lg:grid-cols-5">
          <Reveal className="lg:col-span-2">
            <div className="flex h-full flex-col gap-5">
              <div className="rounded-3xl border border-emerald-900/10 bg-[#F8FFF8] p-8">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1E3A8A] text-white">
                  <MapPin className="h-6 w-6" />
                </div>
                <h3 className="font-heading mt-5 text-xl font-semibold text-slate-900">{SITE.fullName}</h3>
                {SITE.addressLines.map((line) => (
                  <p key={line} data-testid="contact-address-line" className="mt-1 text-sm text-slate-600">
                    {line}
                  </p>
                ))}
                <a
                  data-testid="contact-email-link"
                  href={`mailto:${SITE.email}`}
                  className="mt-4 flex items-center gap-2 text-sm font-medium text-[#1E3A8A] transition-colors hover:text-[#2E8B57]"
                >
                  <Mail className="h-4 w-4" />
                  {SITE.email}
                </a>
                <a
                  data-testid="contact-instagram-link"
                  href={SITE.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center gap-2 text-sm font-medium text-[#1E3A8A] transition-colors hover:text-[#2E8B57]"
                >
                  <Instagram className="h-4 w-4" />
                  @{SITE.instagram}
                </a>
              </div>

              <a
                data-testid="whatsapp-quick-book-button"
                href={waLink("Namaste! I would like to book a session at NAVYOGA, Port Blair.")}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center justify-between rounded-3xl bg-[#2E8B57] p-7 text-white shadow-[0_20px_45px_-18px_rgba(46,139,87,0.6)] transition-[transform,background-color] duration-300 hover:-translate-y-1 hover:bg-[#267a4b]"
              >
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.25em] text-white/70">WhatsApp Booking</p>
                  <p className="font-heading mt-1.5 text-2xl font-semibold">{SITE.phoneDisplay}</p>
                  <p className="mt-1 text-sm text-white/80">Fastest way to reserve your mat</p>
                </div>
                <MessageCircle className="h-9 w-9 transition-transform duration-300 group-hover:scale-110" />
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.12} className="lg:col-span-3">
            <form
              data-testid="contact-booking-form"
              onSubmit={submit}
              className="rounded-3xl border border-emerald-900/10 bg-[#F8FFF8] p-8 sm:p-10"
            >
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Your Name *
                  </label>
                  <input id="contact-name" data-testid="contact-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Phone / WhatsApp *
                  </label>
                  <input id="contact-phone" data-testid="contact-phone-input" required minLength={6} value={form.phone} onChange={set("phone")} placeholder="Your number" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Email (optional)
                  </label>
                  <input id="contact-email" data-testid="contact-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} />
                </div>
                <div>
                  <label htmlFor="contact-therapy" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Therapy of Interest
                  </label>
                  <select id="contact-therapy" data-testid="contact-therapy-select" value={form.therapy} onChange={set("therapy")} className={inputCls}>
                    <option value="">Choose a therapy…</option>
                    {THERAPIES.map((t) => (
                      <option key={t.id} value={t.name}>
                        {t.name}
                      </option>
                    ))}
                    <option value="Group Classes">Group Classes</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-message" className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">
                    Your Message *
                  </label>
                  <textarea id="contact-message" data-testid="contact-message-input" required minLength={5} rows={4} value={form.message} onChange={set("message")} placeholder="Tell us what you're looking for — a health concern, a class timing, anything." className={`${inputCls} resize-none`} />
                </div>
              </div>
              <button
                data-testid="contact-submit-button"
                type="submit"
                disabled={sending}
                className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#1E3A8A] px-8 py-4 text-sm font-semibold text-white shadow-[0_15px_35px_-12px_rgba(30,58,138,0.55)] transition-[transform,background-color,opacity] duration-300 hover:-translate-y-0.5 hover:bg-[#2E8B57] disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending…" : "Send Message"}
              </button>
            </form>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 overflow-hidden rounded-3xl border border-emerald-900/10 shadow-[0_25px_60px_-30px_rgba(30,58,138,0.3)]">
            <iframe
              data-testid="contact-map-iframe"
              title="NAVYOGA location — Port Blair, Andaman and Nicobar Islands"
              src={`https://www.google.com/maps?q=${encodeURIComponent(SITE.mapQuery)}&output=embed`}
              className="h-80 w-full sm:h-96"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </Reveal>
      </div>
    </section>
  );
};
